import { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Column } from '.';
import { cn } from '../../helpers/classnames';
import { IconExclamationTriangle } from '../../icons';

// AG Grid wraps every cell's content in a (non-full-height) `.ag-cell-wrapper`
// because the grid has `enableCellTextSelection` on. That wrapper collapses a
// renderer's `height: 100%`, so vertical centering has to be applied to the cell
// itself (which is always full-height) via the column's `cellStyle`.
export const errorReportIndicatorCellStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const messages = defineMessages({
  open_error_reports: {
    id: 'table.error_report_cell_label',
    defaultMessage:
      '{count, plural, one {Open # active error report} other {Open # active error reports}}',
  },
});

interface ErrorReportIndicatorProps {
  colDef: Column<Entity>;
  data: Entity;
  value: number;
}

const ErrorReportIndicator: FC<ErrorReportIndicatorProps> = ({
  data: rowData,
  value,
  colDef: { onClick },
}) => {
  const intl = useIntl();

  // No active error reports for this entity, leave the cell empty.
  if (!value) {
    return null;
  }

  const content = (
    <>
      <IconExclamationTriangle className="size-4" aria-hidden="true" />
      {value > 1 && (
        <span className="text-sm font-semibold leading-none">{value}</span>
      )}
    </>
  );

  if (!onClick) {
    return (
      <div className="flex items-center justify-center gap-1 text-red-500">
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      // Opt this cell out of the table's row-click navigation (see the row
      // click handler in EntityOverview); clicking it should only open the
      // error report dialog.
      data-suppress-row-click="true"
      aria-label={intl.formatMessage(messages.open_error_reports, {
        count: value,
      })}
      className={cn(
        'flex cursor-pointer items-center justify-center gap-1 border-0 bg-transparent p-0 text-red-500',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600',
      )}
      onClick={(e) => {
        onClick(rowData);
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      {content}
    </button>
  );
};

export default ErrorReportIndicator;
